import React, { forwardRef } from "react";

import { cn } from "@/shadcn/lib/utils";

export type TextInputProps = {
  readonly "id"?: string;
  readonly "name": string;
  readonly "value": string;
  readonly "placeholder"?: string;
  readonly "type"?: string;
  readonly "className"?: string;
  readonly "disabled"?: boolean;
  readonly "onChange"?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  readonly "autoComplete"?: "on" | "off";
  readonly "onBlur"?: (e: React.FocusEvent<HTMLInputElement>) => void;
  readonly "onFocus"?: (e: React.FocusEvent<HTMLInputElement>) => void;
  readonly "aria-invalid"?: boolean;
  readonly "aria-describedby": string;
  readonly "leftIcon"?: React.ReactNode;
  readonly "rightIcon"?: React.ReactNode;
  readonly "readOnly"?: boolean;
  readonly "min"?: string | number;
  readonly "max"?: string | number;
  readonly "step"?: string | number;
  readonly "onClick"?: (e: React.MouseEvent<HTMLInputElement>) => void;
  readonly "inputMode"?:
    | "none"
    | "text"
    | "decimal"
    | "numeric"
    | "email"
    | "url"
    | "search"
    | "tel";
  readonly "fontWeight"?: "normal" | "bold";
  readonly "variant"?: "v3" | "default";
};

const TextInput = forwardRef<HTMLInputElement, TextInputProps>((props, ref): React.ReactElement => {
  const {
    id,
    name,
    placeholder,
    type = "text",
    className = "",
    disabled,
    autoComplete = "on",
    onBlur,
    onFocus,
    onChange,
    value,
    leftIcon,
    rightIcon,
    readOnly,
    min,
    max,
    step,
    inputMode = "text",
    onClick,
    fontWeight = "normal",
    variant = "default",
  } = props;

  return (
    <div className="relative" data-testid="text-input">
      {leftIcon && (
        <div className="absolute top-1/2 left-2 flex h-6 w-6 -translate-y-1/2 items-center justify-center">
          {leftIcon}
        </div>
      )}
      <input
        aria-describedby={props["aria-describedby"]}
        aria-invalid={props["aria-invalid"]}
        autoComplete={autoComplete}
        className={cn(
          "peer block w-full appearance-none border border-border bg-card px-4 type-body-md text-foreground ring-offset-1 outline-hidden placeholder:text-muted focus:border-primary focus:ring-1 focus:ring-primary disabled:pointer-events-none disabled:opacity-50",
          props["aria-invalid"] &&
            "border-destructive ring-1 ring-destructive placeholder:text-destructive",
          "h-10 rounded",
          leftIcon && "pl-10",
          rightIcon && "pr-10",
          disabled && "pointer-events-none opacity-50",
          variant === "v3"
            ? "h-12 rounded-2xl focus:ring-primary!"
            : "h-10 rounded bg-white focus:ring-black!",
          fontWeight === "bold" ? "font-bold" : "font-normal",
          className,
        )}
        disabled={disabled}
        id={id}
        inputMode={inputMode}
        max={max}
        min={min}
        name={name}
        placeholder={placeholder}
        readOnly={disabled || readOnly}
        ref={ref}
        step={step}
        type={type}
        value={value}
        onBlur={onBlur}
        onChange={(event) => {
          if (type === "number") {
            event.currentTarget.value = event.currentTarget.value.replace(/^(-?)0+(?=\d)/, "$1");
          }
          onChange?.(event);
        }}
        onClick={onClick}
        onFocus={onFocus}
      />
      {rightIcon && <div className="absolute top-1/2 right-3 -translate-y-1/2">{rightIcon}</div>}
    </div>
  );
});

TextInput.displayName = "TextInput";

export default TextInput;
